<template>
  <div class="mt-component-form">
    <vs-row class="my-4 header-flex">
      <h5><i class="dot"></i>유해·위험요인 개선 결과</h5>
      <div class="header-buttons" v-if="!readonly && !isPrint">
        <!-- Desktop: 개선내역 추가 + 선택삭제 -->
        <vs-button class="desktop-only" color="secondary" type="border" @click="handleAddRow">개선내역 추가</vs-button>
        <!-- PC/Mobile 공통: 선택삭제 -->
        <vs-button class="ml-4" color="secondary" :disabled="selectedItemsCount === 0" @click="handleRemoveRowConfirm">선택삭제</vs-button>
      </div>
    </vs-row>

    <!-- 빈 목록 안내 메시지 -->
    <div v-if="list.length === 0 && !isPrint" class="empty-list-message">
      <div class="empty-content">
        <i class="feather icon-inbox empty-icon"></i>
        <p class="empty-text">개선내역을 추가해주세요</p>
        <vs-button color="primary" type="filled" @click="handleAddRow" class="empty-add-btn">
          <i class="feather icon-plus mr-2"></i>
          첫 번째 개선내역 추가
        </vs-button>
      </div>
    </div>

    <!-- Desktop 테이블 레이아웃 -->
    <div class="desktop-layout">
      <div v-for="(item, item_i) in list" :key="`improvement-${item_i}`" :class="item_i > 0 ? 'mt-4' : ''">
        <p class="mb-2">- 개선내역 {{ item_i + 1 }}</p>
        <table>
          <colgroup>
            <col width="36" />
            <col />
            <col />
            <col width="83" />
            <col width="83" />
            <col width="114" />
            <col />
            <col />
            <col width="83" />
            <col width="83" />
            <col width="114" />
          </colgroup>
          <tbody>
            <tr>
              <th rowspan="7">
                <vs-checkbox v-if="!isPrint" v-model="checkData" :vs-value="item_i" class="justify-center mx-0" />
              </th>
              <th>공정명</th>
              <td colspan="9" style="text-align: start">
                <span v-if="isPrint" class="text-left" style="font-weight: 400">{{ item.공정명 }}</span>
                <ml-input v-else :readonly="readonly" class="w-full text-left" v-model="item.공정명" />
              </td>
            </tr>
            <tr>
              <th>위험분류</th>
              <td colspan="9" style="text-align: start">
                <span v-if="isPrint" class="w-full text-left" style="font-weight: 400">{{ item.위험분류 === "직접입력" ? item.위험분류직접입력 : item.위험분류 }}</span>
                <div v-else class="flex items-center gap-2">
                  <vs-select v-model="item.위험분류" autocomplete :disabled="readonly">
                    <vs-select-item v-for="_si in 위험분류option" :key="_si" :value="_si" :text="_si" />
                  </vs-select>
                  <ml-input v-if="item.위험분류 === '직접입력'" :readonly="readonly" class="flex-1" v-model="item.위험분류직접입력" />
                </div>
              </td>
            </tr>
            <tr>
              <th>유해·위험 요인 파악</th>
              <td colspan="9" style="text-align: start">
                <span v-if="isPrint" class="w-full text-left" style="font-weight: 400">{{ item.유해위험요인파악 }}</span>
                <ml-input v-else :readonly="readonly" class="w-full text-left" v-model="item.유해위험요인파악" />
              </td>
            </tr>
            <tr>
              <th colspan="5">개선 전</th>
              <th colspan="5">개선 후</th>
            </tr>
            <tr>
              <td colspan="5">
                <photo-upload-grid-module :isShowTitle="false" :isCheckMode="false" :showFooterInput="false" :row-count="2" :grid-count="4" v-model="item.개선전사진" :isPrint="isPrint" :readonly="readonly" />
              </td>
              <td colspan="5">
                <photo-upload-grid-module :isShowTitle="false" :isCheckMode="false" :showFooterInput="false" :row-count="2" :grid-count="4" v-model="item.개선후사진" :isPrint="isPrint" :readonly="readonly" />
              </td>
            </tr>
            <tr>
              <th colspan="2">감소대책</th>
              <th>빈도</th>
              <th>강도</th>
              <th>위험도</th>
              <th colspan="2">개선내용</th>
              <th>빈도</th>
              <th>강도</th>
              <th>위험도</th>
            </tr>
            <tr>
              <td colspan="2">
                <span v-if="isPrint" class="w-full text-left print-textarea" style="font-weight: 400">{{ item.감소대책 }}</span>
                <ml-textarea v-else :readonly="readonly" v-model="item.감소대책" />
              </td>
              <td>
                <span v-if="isPrint" class="w-full text-left" style="font-weight: 400">{{ item.감소빈도 }}</span>
                <vs-select v-else v-model="item.감소빈도" autocomplete :disabled="readonly" class="w-full" @input="changeRisk(item_i, '감소')">
                  <vs-select-item v-for="n in 5" :key="`before-freq-desktop-${n}`" :value="n" :text="n" />
                </vs-select>
              </td>
              <td>
                <span v-if="isPrint" class="w-full text-left" style="font-weight: 400">{{ item.감소강도 }}</span>
                <vs-select v-else v-model="item.감소강도" autocomplete :disabled="readonly" class="w-full" @input="changeRisk(item_i, '감소')">
                  <vs-select-item v-for="n in 5" :key="`before-intensity-desktop-${n}`" :value="n" :text="n" />
                </vs-select>
              </td>
              <td>
                <span v-if="isPrint" class="w-full text-left" style="font-weight: 400" :style="{ color: riskColor(item.감소위험도) }">{{ item.감소위험도 }}</span>
                <ml-input v-else class="w-full" readonly v-model="item.감소위험도" :style="{ color: riskColor(item.감소위험도) }" />
              </td>

              <td colspan="2">
                <div class="flex items-center mb-3">
                  <div class="w-24">담당자 :</div>
                  <span v-if="isPrint" class="flex-1 text-left" style="font-weight: 400">{{ item.개선내용담당자 }}</span>
                  <ml-input v-else class="flex-1 text-left" :isPrint="isPrint" :readonly="readonly" v-model="item.개선내용담당자" />
                </div>
                <div class="flex items-center mb-3">
                  <p class="w-24">개선일자 :</p>
                  <span v-if="isPrint" class="flex-1 text-left" style="font-weight: 400">{{ item.개선내용개선일자 }}</span>
                  <DateSelector v-else class="flex-1 text-left" :isPrint="isPrint" :readonly="readonly" v-model="item.개선내용개선일자" />
                </div>
                <span v-if="isPrint" class="w-full text-left print-textarea" style="font-weight: 400">{{ item.개선내용 }}</span>
                <ml-textarea v-else :readonly="readonly" placeholder="개선내용" v-model="item.개선내용" />
              </td>
              <td>
                <span v-if="isPrint" class="w-full text-left" style="font-weight: 400">{{ item.개선빈도 }}</span>
                <vs-select v-else v-model="item.개선빈도" autocomplete :disabled="readonly" class="w-full" @input="changeRisk(item_i, '개선')">
                  <vs-select-item v-for="n in 5" :key="`after-freq-desktop-${n}`" :value="n" :text="n" />
                </vs-select>
              </td>
              <td>
                <span v-if="isPrint" class="w-full text-left" style="font-weight: 400">{{ item.개선강도 }}</span>
                <vs-select v-else v-model="item.개선강도" autocomplete :disabled="readonly" class="w-full" @input="changeRisk(item_i, '개선')">
                  <vs-select-item v-for="n in 5" :key="`after-intensity-desktop-${n}`" :value="n" :text="n" />
                </vs-select>
              </td>
              <td>
                <span v-if="isPrint" class="w-full text-left" style="font-weight: 400" :style="{ color: riskColor(item.개선위험도) }">{{ item.개선위험도 }}</span>
                <ml-input v-else class="w-full" readonly v-model="item.개선위험도" :style="{ color: riskColor(item.개선위험도) }" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <vs-row class="my-4 header-flex">
        <h5></h5>
        <div class="header-buttons" v-if="!readonly && !isPrint">
          <!-- Desktop: 개선내역 추가 + 선택삭제 -->
          <vs-button class="desktop-only" color="secondary" @click="handleAddRow">개선내역 추가</vs-button>
          <!-- PC/Mobile 공통: 선택삭제 -->
          <vs-button class="ml-4" color="secondary" :disabled="selectedItemsCount === 0" @click="handleRemoveRowConfirm">선택삭제</vs-button>
        </div>
      </vs-row>
    </div>

    <!-- Mobile 카드 레이아웃 -->
    <div class="mobile-layout">
      <!-- 모바일 전체 선택 헤더 (아이템이 있을 때만) -->
      <div class="mobile-header" v-if="!isPrint && list.length > 0" @click="toggleAllCheck">
        <vs-checkbox :value="allChecked" />
        <span class="mobile-header-text">전체 선택</span>
      </div>

      <!-- 모바일 카드형 아이템들 -->
      <div class="mobile-cards-container">
        <div v-for="(item, item_i) in list" :key="`improvement-mobile-${item_i}`" class="mobile-card" :class="{ selected: checkData.includes(item_i), 'print-mode': isPrint }">
          <!-- 카드 헤더 (체크박스 + 인덱스 + 삭제 버튼) -->
          <div class="mobile-card-header" v-if="!isPrint">
            <div class="mobile-card-header-left">
              <vs-checkbox v-model="checkData" :vs-value="item_i"></vs-checkbox>
              <span class="card-index">개선내역 {{ item_i + 1 }}</span>
            </div>
            <vs-button class="mobile-delete-btn" color="danger" type="flat" size="small" @click="handleDeleteSingleItem(item_i)"> 삭제 </vs-button>
          </div>
          <div class="mobile-card-header print-header" v-else>
            <span class="card-index">개선내역 {{ item_i + 1 }}</span>
          </div>

          <!-- 카드 내용 -->
          <div class="mobile-card-content">
            <!-- 공정명 -->
            <div class="mobile-field">
              <label class="mobile-field-label">공정명</label>
              <div class="mobile-field-input">
                <ml-input v-if="!isPrint" :readonly="readonly" class="w-full" v-model="item.공정명" />
                <span v-else class="mobile-print-value">{{ item.공정명 }}</span>
              </div>
            </div>

            <!-- 위험분류 -->
            <div class="mobile-field">
              <label class="mobile-field-label">위험분류</label>
              <div class="mobile-field-input">
                <div v-if="!isPrint" class="flex items-center gap-2">
                  <vs-select v-model="item.위험분류" autocomplete :disabled="readonly" class="flex-1">
                    <vs-select-item v-for="_si in 위험분류option" :key="_si" :value="_si" :text="_si" />
                  </vs-select>
                  <ml-input v-if="item.위험분류 === '직접입력'" :readonly="readonly" class="flex-1" v-model="item.위험분류직접입력" />
                </div>
                <span v-else class="mobile-print-value">{{ item.위험분류 === "직접입력" ? item.위험분류직접입력 : item.위험분류 }}</span>
              </div>
            </div>

            <!-- 유해·위험 요인 파악 -->
            <div class="mobile-field">
              <label class="mobile-field-label">유해·위험 요인 파악</label>
              <div class="mobile-field-input">
                <ml-input v-if="!isPrint" :readonly="readonly" class="w-full" v-model="item.유해위험요인파악" />
                <span v-else class="mobile-print-value">{{ item.유해위험요인파악 }}</span>
              </div>
            </div>

            <!-- 개선 전/후 탭 -->
            <div class="mobile-improvement-tabs">
              <div class="tab-headers">
                <div class="tab-header" :class="{ active: activeTab[item_i] === 'before' }" @click="setActiveTab(item_i, 'before')">개선 전 {{ getTabRiskText(item, "감소") }}</div>
                <div class="tab-header" :class="{ active: activeTab[item_i] === 'after' }" @click="setActiveTab(item_i, 'after')">개선 후 {{ getTabRiskText(item, "개선") }}</div>
              </div>

              <div class="tab-content">
                <!-- 개선 전 탭 내용 -->
                <div v-if="activeTab[item_i] === 'before'" class="tab-panel">
                  <!-- 개선전 사진 -->
                  <div class="mobile-field">
                    <label class="mobile-field-label">개선 전 사진</label>
                    <div class="mobile-field-input">
                      <photo-upload-grid-module :isShowTitle="false" :isCheckMode="false" :showFooterInput="false" :row-count="2" :grid-count="4" v-model="item.개선전사진" :isPrint="isPrint" :readonly="readonly" />
                    </div>
                  </div>

                  <!-- 감소대책 -->
                  <div class="mobile-field">
                    <label class="mobile-field-label">감소대책</label>
                    <div class="mobile-field-input">
                      <ml-textarea v-if="!isPrint" :readonly="readonly" v-model="item.감소대책" class="w-full" rows="3" />
                      <span v-else class="mobile-print-value mobile-print-textarea">{{ item.감소대책 }}</span>
                    </div>
                  </div>

                  <!-- 감소 빈도/강도 -->
                  <div class="mobile-field-row">
                    <div class="mobile-field mobile-field-half">
                      <label class="mobile-field-label">빈도</label>
                      <div class="mobile-field-input">
                        <vs-select v-if="!isPrint" v-model="item.감소빈도" autocomplete :disabled="readonly" class="w-full" @input="changeRisk(item_i, '감소')">
                          <vs-select-item v-for="n in 5" :key="`before-freq-${n}`" :value="n" :text="n" />
                        </vs-select>
                        <span v-else class="mobile-print-value">{{ item.감소빈도 }}</span>
                      </div>
                    </div>
                    <div class="mobile-field mobile-field-half">
                      <label class="mobile-field-label">강도</label>
                      <div class="mobile-field-input">
                        <vs-select v-if="!isPrint" v-model="item.감소강도" autocomplete :disabled="readonly" class="w-full" @input="changeRisk(item_i, '감소')">
                          <vs-select-item v-for="n in 5" :key="`before-intensity-${n}`" :value="n" :text="n" />
                        </vs-select>
                        <span v-else class="mobile-print-value">{{ item.감소강도 }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- 감소 위험도 -->
                  <div class="mobile-field">
                    <label class="mobile-field-label">위험도</label>
                    <div class="mobile-field-input">
                      <ml-input v-if="!isPrint" class="w-full" readonly v-model="item.감소위험도" :style="{ color: riskColor(item.감소위험도) }" />
                      <span v-else class="mobile-print-value" :style="{ color: riskColor(item.감소위험도) }">{{ item.감소위험도 }}</span>
                    </div>
                  </div>
                </div>

                <!-- 개선 후 탭 내용 -->
                <div v-if="activeTab[item_i] === 'after'" class="tab-panel">
                  <!-- 개선후 사진 -->
                  <div class="mobile-field">
                    <label class="mobile-field-label">개선 후 사진</label>
                    <div class="mobile-field-input">
                      <photo-upload-grid-module :isShowTitle="false" :isCheckMode="false" :showFooterInput="false" :row-count="2" :grid-count="4" v-model="item.개선후사진" :isPrint="isPrint" :readonly="readonly" />
                    </div>
                  </div>

                  <!-- 개선내용 -->
                  <div class="mobile-field">
                    <label class="mobile-field-label">개선내용</label>
                    <div class="mobile-field-input">
                      <div class="flex items-center mb-3">
                        <div class="w-24">담당자 :</div>
                        <span v-if="isPrint" class="flex-1 text-left" style="font-weight: 400">{{ item.개선내용담당자 }}</span>
                        <ml-input v-else class="flex-1 text-left" :isPrint="isPrint" :readonly="readonly" v-model="item.개선내용담당자" />
                      </div>
                    </div>
                    <div class="mobile-field-input">
                      <div class="flex items-center mb-3">
                        <p class="w-24">개선일자 :</p>
                        <span v-if="isPrint" class="flex-1 text-left" style="font-weight: 400">{{ item.개선내용개선일자 }}</span>
                        <DateSelector v-else class="flex-1 text-left" :isPrint="isPrint" :readonly="readonly" v-model="item.개선내용개선일자" />
                      </div>
                    </div>
                    <div class="mobile-field-input">
                      <ml-textarea v-if="!isPrint" :readonly="readonly" v-model="item.개선내용" class="w-full" rows="3" />
                      <span v-else class="mobile-print-value mobile-print-textarea">{{ item.개선내용 }}</span>
                    </div>
                  </div>

                  <!-- 개선 빈도/강도 -->
                  <div class="mobile-field-row">
                    <div class="mobile-field mobile-field-half">
                      <label class="mobile-field-label">빈도</label>
                      <div class="mobile-field-input">
                        <vs-select v-if="!isPrint" v-model="item.개선빈도" autocomplete :disabled="readonly" class="w-full" @input="changeRisk(item_i, '개선')">
                          <vs-select-item v-for="n in 5" :key="`after-freq-${n}`" :value="n" :text="n" />
                        </vs-select>
                        <span v-else class="mobile-print-value">{{ item.개선빈도 }}</span>
                      </div>
                    </div>
                    <div class="mobile-field mobile-field-half">
                      <label class="mobile-field-label">강도</label>
                      <div class="mobile-field-input">
                        <vs-select v-if="!isPrint" v-model="item.개선강도" autocomplete :disabled="readonly" class="w-full" @input="changeRisk(item_i, '개선')">
                          <vs-select-item v-for="n in 5" :key="`after-intensity-${n}`" :value="n" :text="n" />
                        </vs-select>
                        <span v-else class="mobile-print-value">{{ item.개선강도 }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- 개선 위험도 -->
                  <div class="mobile-field">
                    <label class="mobile-field-label">위험도</label>
                    <div class="mobile-field-input">
                      <ml-input v-if="!isPrint" class="w-full" readonly v-model="item.개선위험도" :style="{ color: riskColor(item.개선위험도) }" />
                      <span v-else class="mobile-print-value" :style="{ color: riskColor(item.개선위험도) }">{{ item.개선위험도 }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 모바일 하단 개선내역 추가 버튼 (아이템이 있을 때만) -->
      <div class="mobile-add-button-container" v-if="!isPrint && list.length > 0">
        <vs-button class="mobile-add-btn" color="primary" type="filled" size="large" @click="handleAddRow">
          <i class="feather icon-plus mr-2"></i>
          개선내역 추가
        </vs-button>
      </div>
    </div>

    <!-- 삭제 확인 팝업 -->
    <vs-popup class="popup-example" title="삭제 확인" :active.sync="showDeleteConfirm">
      <div class="popup-content">
        <p class="delete-message">{{ deleteMessage }}</p>
        <div class="popup-buttons">
          <vs-button color="danger" type="filled" @click="confirmDelete"> 삭제 </vs-button>
          <vs-button color="dark" type="flat" @click="cancelDelete"> 취소 </vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import PhotoUploadGridModule from "@/views/modules/PhotoUploadGridModule";
import DateSelector from "@/components/selector/DateSelector";

export default {
  components: { PhotoUploadGridModule, DateSelector },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    readonly: Boolean,
    isPrint: Boolean,
  },
  data() {
    return {
      위험분류option: ["기계(설비)적 요인", "전기적 요인", "화학(물질)적 요인", "생물학적 요인", "작업특성 요인", "작업환경 요인", "직접입력"],
      checkData: [],
      list: this.value,
      showDeleteConfirm: false,
      deleteMessage: "",
      deleteAction: null,
      deleteIndex: null,
      activeTab: {}, // 각 아이템별 활성 탭을 관리
    };
  },
  watch: {
    value: {
      handler(value) {
        this.list = value;
      },
    },
    list: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value.length < 1) this.handleAddRow();
        this.initializeActiveTabs();
        this.$emit("input", value);
      },
    },
  },
  computed: {
    selectedItemsCount() {
      return this.checkData.length;
    },
    allChecked() {
      return this.list.length > 0 && this.checkData.length === this.list.length;
    },
  },
  methods: {
    // 초기화 메서드들
    initializeActiveTabs() {
      this.list.forEach((_, index) => {
        if (!this.activeTab[index]) {
          this.$set(this.activeTab, index, "before");
        }
      });
    },

    // 항목 추가
    handleAddRow() {
      const newIndex = this.list.length;
      this.list.push({
        개선내용: "",
        개선내용담당자: "",
        개선내용개선일자: "",
        개선강도: "",
        개선빈도: "",
        개선위험도: "",
        개선전사진: [],
        개선후사진: [],
        공정명: "",
        감소강도: "",
        감소대책: "",
        감소빈도: "",
        감소위험도: "",
        위험분류: "",
        위험분류직접입력: "",
        유해위험요인파악: "",
      });
      this.$set(this.activeTab, newIndex, "before");
    },

    // 선택삭제 확인
    handleRemoveRowConfirm() {
      const selectedCount = this.checkData.length;
      if (selectedCount === 0) {
        this.$vs.notify({
          title: "알림",
          text: "삭제할 항목을 선택해주세요.",
          color: "warning",
        });
        return;
      }

      this.deleteMessage = `선택된 ${selectedCount}개의 개선내역을 삭제하시겠습니까?`;
      this.deleteAction = "batch";
      this.showDeleteConfirm = true;
    },

    // 개별 항목 삭제 확인
    handleDeleteSingleItem(index) {
      this.deleteMessage = `${index + 1}번째 개선내역을 삭제하시겠습니까?`;
      this.deleteAction = "single";
      this.deleteIndex = index;
      this.showDeleteConfirm = true;
    },

    // 삭제 확인
    confirmDelete() {
      if (this.deleteAction === "batch") {
        this.handleRemoveRow();
      } else if (this.deleteAction === "single") {
        this.handleRemoveSingleRow();
      }
      this.showDeleteConfirm = false;
    },

    // 삭제 취소
    cancelDelete() {
      this.showDeleteConfirm = false;
      this.deleteAction = null;
      this.deleteIndex = null;
      this.deleteMessage = "";
    },

    // 선택삭제 실행
    handleRemoveRow() {
      // 삭제할 인덱스들을 내림차순으로 정렬하여 인덱스 오류 방지
      const sortedIndexes = [...this.checkData].sort((a, b) => b - a);

      sortedIndexes.forEach((index) => {
        this.list.splice(index, 1);
        // activeTab에서도 해당 인덱스 삭제
        this.$delete(this.activeTab, index);
      });

      // activeTab 인덱스 재정렬
      this.reindexActiveTabs();

      this.checkData = [];

      if (this.list.length < 1) this.handleAddRow();

      this.$vs.notify({
        title: "삭제 완료",
        text: "선택된 개선내역이 삭제되었습니다.",
        color: "success",
      });
    },

    // 개별 항목 삭제 실행
    handleRemoveSingleRow() {
      this.list.splice(this.deleteIndex, 1);
      this.$delete(this.activeTab, this.deleteIndex);
      this.reindexActiveTabs();

      if (this.list.length < 1) this.handleAddRow();

      this.$vs.notify({
        title: "삭제 완료",
        text: "개선내역이 삭제되었습니다.",
        color: "success",
      });
    },

    // activeTab 인덱스 재정렬
    reindexActiveTabs() {
      const newActiveTab = {};
      this.list.forEach((_, index) => {
        newActiveTab[index] = this.activeTab[index] || "before";
      });
      this.activeTab = newActiveTab;
    },

    // 전체 선택 토글
    toggleAllCheck() {
      if (this.allChecked) {
        this.checkData = [];
      } else {
        this.checkData = this.list.map((_, index) => index);
      }
    },

    // 탭 변경
    setActiveTab(index, tab) {
      if (this.isPrint) return;
      this.$set(this.activeTab, index, tab);
    },

    // 탭 레이블에 위험도 표시
    getTabRiskText(item, type) {
      const riskValue = item[`${type}위험도`];
      if (riskValue && riskValue !== "") {
        return `(${riskValue})`;
      }
      return "";
    },

    changeRisk(index, type) {
      this.$nextTick(() => {
        const risk = this.list[index][`${type}강도`] * this.list[index][`${type}빈도`];

        if (risk === 0) return;

        let riskKo = "";
        if (risk <= 3) riskKo = "매우낮음";
        else if (risk <= 6) riskKo = "낮음";
        else if (risk === 8) riskKo = "보통";
        else if (risk <= 12) riskKo = "약간높음";
        else if (risk === 15) riskKo = "높음";
        else if (15 < risk) riskKo = "매우높음";

        this.$set(this.list[index], `${type}위험도`, `${riskKo}(${risk})`);
      });
    },

    riskColor(risk) {
      if (risk.includes("매우낮음")) return "#28C76F";
      else if (risk.includes("낮음")) return "#FF8510";
      else return "#EA5455";
    },
  },
};
</script>

<style lang="scss" scoped>
$border: 1px solid #cdcdcd;
$bg: #f9f9f9;
$mobile-breakpoint: 768px;
$mobile-card-bg: #ffffff;
$mobile-card-border: 1px solid #e8e8e8;
$mobile-card-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
$mobile-selected-bg: #f0f9ff;
$mobile-selected-border: 1px solid #3b82f6;

.mt-component-form {
  margin-top: 25px;
}

.header-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-buttons {
  display: flex;
  align-items: center;
}

.desktop-only {
  display: inline-flex;
}

/* 반응형 레이아웃 제어 */
.desktop-layout {
  display: block;
}
.mobile-layout {
  display: none;
}

/* 빈 목록 안내 메시지 */
.empty-list-message {
  padding: 60px 20px;
  text-align: center;
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  margin-bottom: 20px;
}

.empty-content {
  max-width: 300px;
  margin: 0 auto;
}

.empty-icon {
  font-size: 48px;
  color: #94a3b8;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  color: #64748b;
  margin-bottom: 24px;
  font-weight: 500;
}

.empty-add-btn {
  min-height: 44px;
  padding: 12px 24px;
  font-weight: 600;

  .feather {
    font-size: 16px;
  }
}

/* Desktop Table Styles */
table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;

  th,
  td {
    font-weight: 700;
    font-size: 14px;
    line-height: 16.8px;
    text-align: center;
    border: $border;
  }

  th {
    background: $bg;
    padding: 16px 2px;
  }
  td {
    padding: 7px;
  }
}

.print-textarea {
  white-space: pre-wrap;
  line-height: 1.5;
  text-align: left;
  display: block;
  padding: 8px;
}

/* Mobile Styles */
@media (max-width: $mobile-breakpoint) {
  .desktop-layout {
    display: none;
  }
  .mobile-layout {
    display: block;
  }

  /* 모바일에서 개선내역 추가 버튼 숨김 */
  .desktop-only {
    display: none !important;
  }

  /* 모바일 헤더 최적화 */
  .header-flex {
    .header-buttons {
      .vs-button {
        min-height: 40px;
        font-size: 14px;
        padding: 8px 16px;
      }
    }
  }

  /* 빈 목록 메시지 모바일 최적화 */
  .empty-list-message {
    padding: 40px 16px;
    margin-bottom: 16px;
  }

  .empty-icon {
    font-size: 40px;
  }

  .empty-text {
    font-size: 15px;
  }
}

.mobile-header {
  display: flex;
  align-items: center;
  padding: 15px;
  background-color: $bg;
  border: $border;
  border-radius: 8px;
  margin-bottom: 15px;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background-color: #f1f5f9;
    border-color: #94a3b8;
  }

  &:active {
    transform: scale(0.98);
  }

  .mobile-header-text {
    font-weight: 500;
    font-size: 16px;
    color: #374151;
    flex: 1;
  }

  .vs-checkbox {
    transform: scale(1.2);
    pointer-events: none;
  }
}

.mobile-cards-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.mobile-card {
  background: $mobile-card-bg;
  border: $mobile-card-border;
  border-radius: 12px;
  box-shadow: $mobile-card-shadow;
  overflow: hidden;
  transition: all 0.3s ease;

  &.selected {
    background: $mobile-selected-bg;
    border: $mobile-selected-border;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  }

  &.print-mode {
    box-shadow: none;
    border: $border;
    margin-bottom: 10px;
  }
}

.mobile-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;

  &.print-header {
    background: $bg;
    justify-content: center;
  }

  .mobile-card-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .card-index {
    font-weight: 600;
    font-size: 16px;
    color: #1e293b;
  }

  .vs-checkbox {
    transform: scale(1.2);
  }

  .mobile-delete-btn {
    min-height: 32px;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3);
    }
  }
}

.mobile-card-content {
  padding: 20px;
}

.mobile-field {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.mobile-field-row {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.mobile-field-third {
  flex: 1;
  margin-bottom: 0;
}

.mobile-field-half {
  flex: 1;
  margin-bottom: 0;
}

.mobile-field-label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
  line-height: 1.4;
}

.mobile-field-input {
  .vs-select,
  .vs-input,
  .vs-textarea {
    min-height: 44px;

    .vs-select__input,
    .vs-input__input,
    .vs-textarea__input {
      font-size: 16px;
      padding: 12px 16px;
    }
  }

  .vs-select {
    .vs-select__input {
      border-radius: 8px;
      border: 1px solid #d1d5db;

      &:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
    }
  }

  .vs-textarea {
    .vs-textarea__input {
      border-radius: 8px;
      border: 1px solid #d1d5db;
      min-height: 80px;
      resize: vertical;

      &:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
    }
  }
}

.mobile-print-value {
  display: block;
  padding: 12px 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  min-height: 20px;

  &.mobile-print-textarea {
    min-height: 60px;
    white-space: pre-wrap;
    line-height: 1.5;
  }
}

/* 개선 전/후 탭 스타일 */
.mobile-improvement-tabs {
  margin-top: 20px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.tab-headers {
  display: flex;
  background: #f8fafc;
}

.tab-header {
  flex: 1;
  padding: 12px 16px;
  text-align: center;
  font-weight: 500;
  font-size: 14px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  border-right: 1px solid #e2e8f0;
  user-select: none;

  &:last-child {
    border-right: none;
  }

  &.active {
    background: #3b82f6;
    color: white;
    font-weight: 600;
  }

  &:not(.active):hover {
    background: #e2e8f0;
    color: #374151;
  }
}

.tab-content {
  background: white;
}

.tab-panel {
  padding: 20px;
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 모바일 하단 개선내역 추가 버튼 */
.mobile-add-button-container {
  margin-top: 20px;
  padding: 0 5px;
}

.mobile-add-btn {
  width: 100%;
  min-height: 50px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  .feather {
    font-size: 18px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
  }
}

/* 삭제 확인 팝업 스타일 */
.popup-content {
  padding: 20px;
  text-align: center;
}

.delete-message {
  font-size: 16px;
  color: #374151;
  margin-bottom: 25px;
  line-height: 1.5;
}

.popup-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;

  .vs-button {
    min-width: 100px;
    min-height: 40px;
    font-weight: 600;
  }
}

/* 인쇄 시 PC 레이아웃 강제 적용 */
@media print {
  .desktop-layout {
    display: block !important;
  }
  .mobile-layout {
    display: none !important;
  }

  /* 개선내역 단위별 페이지 분할 최적화 */
  .desktop-layout > div {
    break-inside: avoid;
    page-break-inside: avoid;

    /* 개선내역 제목과 테이블을 함께 유지 */
    p {
      break-after: avoid;
      page-break-after: avoid;
    }

    table {
      break-before: avoid;
      page-break-before: avoid;
    }
  }

  /* 첫 번째 개선내역이 아닌 경우 적절한 간격 유지 */
  .desktop-layout > div + div {
    margin-top: 20px;
    break-before: auto;
    page-break-before: auto;
  }
}

/* 작은 모바일 화면 최적화 */
@media (max-width: 480px) {
  .mobile-card-content {
    padding: 15px;
  }

  /* 빈도/강도는 가로 유지, 위험도만 별도 행으로 */
  .mobile-field-row {
    gap: 10px; /* 가로 배치 유지 */

    .mobile-field-third {
      margin-bottom: 15px;
    }

    .mobile-field-half {
      margin-bottom: 15px;
    }
  }

  .mobile-header {
    padding: 12px;
  }

  .mobile-card-header {
    padding: 12px 15px;

    .mobile-card-header-left {
      .card-index {
        font-size: 14px;
      }
    }

    .mobile-delete-btn {
      min-height: 28px;
      padding: 6px 10px;
      font-size: 11px;
    }
  }

  .mobile-add-btn {
    min-height: 48px;
    font-size: 15px;

    .feather {
      font-size: 16px;
    }
  }

  .popup-buttons {
    flex-direction: column;
    gap: 10px;

    .vs-button {
      width: 100%;
    }
  }

  .tab-header {
    padding: 10px 12px;
    font-size: 13px;
  }

  .tab-panel {
    padding: 15px;
  }
}
</style>
